/**
 * @file Card.tsx
 * @description
 * Represents a single card component in the game.
 * Displays the card value and provides basic interactivity (click).
 * Now supports rendering a face-down card with a count.
 *
 * @dependencies
 * - React
 *
 * @notes
 * - Basic styling applied directly; will integrate theme/CSS later.
 * - Click handler is provided by parent.
 * - Supports indicating if the card is selected for playing.
 * - Can display as a face-down card showing a count instead of a value.
 */

import React from 'react';

interface CardProps {
  /** The numeric value of the card (2-99). */
  value?: number; // Made optional to support face-down cards without a value
  /** Optional click handler for the card. */
  onClick?: (value: number) => void;
  /** Optional boolean to indicate if the card is playable/interactive (e.g., in current player's hand). */
  isPlayable?: boolean;
   /** Optional boolean to indicate if this card is currently selected by the player for playing. */
   isSelected?: boolean;
   /** Optional boolean to indicate if the card should be shown face-down. */
   isFaceDown?: boolean; // Added isFaceDown prop
   /** Optional number to display when the card is face-down (e.g., card count). */
   count?: number; // Added count prop for face-down cards
}

const Card: React.FC<CardProps> = ({ value, onClick, isPlayable = true, isSelected = false, isFaceDown = false, count }) => {

  const handleClick = () => {
    // Only trigger click if the card is not face-down AND is playable AND onClick is provided
    if (!isFaceDown && onClick && isPlayable && value !== undefined) {
      onClick(value);
    }
  };

  // Basic styling - will be replaced by proper CSS classes and theming later
  const cardStyle: React.CSSProperties = {
    width: '60px',
    height: '90px',
    border: isSelected ? '2px solid blue' : (isPlayable && !isFaceDown ? '1px solid #ccc' : '1px solid #eee'), // Highlight selected card
    borderRadius: '5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: isFaceDown ? '1.2em' : '1.5em', // Smaller font for count
    fontWeight: 'bold',
    margin: '5px',
    cursor: !isFaceDown && isPlayable && onClick ? 'pointer' : 'default', // Cursor only for face-up, playable cards
    backgroundColor: isFaceDown ? '#6b7280' : (isSelected ? '#bfdbfe' : (isPlayable ? '#fff' : '#eee')), // Dark grey for face-down
    color: isFaceDown ? '#d1d5db' : (isPlayable ? '#000' : '#aaa'), // Lighter text for face-down
    boxShadow: isSelected ? '0 0 10px rgba(0,0,255,0.5)' : (isPlayable && !isFaceDown ? '2px 2px 5px rgba(0,0,0,0.2)' : 'none'), // Add glow for selected
    transition: 'transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out, background-color 0.1s ease-in-out, border-color 0.1s ease-in-out',
    // Optional: Add a subtle hover effect for playable cards that are NOT selected and NOT face-down
    ...(!isFaceDown && isPlayable && onClick && !isSelected && {
      ':hover': {
        transform: 'translateY(-3px)',
        boxShadow: '3px 3px 7px rgba(0,0,0,0.3)',
      } as React.CSSProperties, // Type assertion needed for nested pseudo-class
    })
  };

  return (
    <div
      style={cardStyle}
      onClick={handleClick}
      aria-label={isFaceDown ? `Face-down card, count ${count}` : `Card with value ${value}${isSelected ? ', Selected' : ''}`}
      role={!isFaceDown && isPlayable && onClick ? "button" : undefined} // Indicate interactive role only for face-up, playable cards
    >
      {isFaceDown ? (count !== undefined ? count : '?') : (value !== undefined ? value : '')}
    </div>
  );
};

export default Card;